/**
 * Univers — Salles de séminaire (iter-52 · Vague 3).
 *
 * Direct booking flow:
 *   1. Pick a room (from /api/meeting-rooms)
 *   2. Date + slot (half-day AM, half-day PM, full-day) with unavailability check
 *   3. Layout + headcount
 *   4. Booker info + setup notes
 *   5. FineoPay checkout
 */
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { Briefcase, Users, Loader2, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api from "../../lib/api";
import { useSiteConfig, sel } from "../../lib/site-config";

const HERO = "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/oy7zzngs_SEMINAIRE.png";

const fmtXof = (n) => new Intl.NumberFormat("fr-FR").format(Number(n || 0)) + " FCFA";

export default function UniversSalles() {
  const navigate = useNavigate();
  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_salles");
  const heroKicker = ph.kicker || "· Corporate ·";
  const heroTitle = ph.title || "Salles de séminaire";
  const heroSubtitle = ph.subtitle_long || "Trois salles modulables, vue lagune, équipées pour vos séminaires, formations et conseils d'administration. Réservation directe en ligne.";
  const [rooms, setRooms] = useState([]);
  const [layoutsCatalog, setLayoutsCatalog] = useState([]);
  const [slotsCatalog, setSlotsCatalog] = useState([]); // [{id,label_fr}]
  const [room, setRoom] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [unavailable, setUnavailable] = useState([]); // slot ids taken
  const [layout, setLayout] = useState("");
  const [pax, setPax] = useState(10);
  const [setupNotes, setSetupNotes] = useState("");
  const [booker, setBooker] = useState({ name: "", surname: "", email: "", phone: "", nationality: "Ivoirien(ne)" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/meeting-rooms").then((r) => setRooms(r.data?.items || []));
    api.get("/experiences/meeting-rooms/catalog").then((r) => {
      setLayoutsCatalog(r.data?.layouts || []);
      setSlotsCatalog(r.data?.slots || []);
    });
  }, []);

  useEffect(() => {
    if (!room || !date) { setUnavailable([]); return; }
    const iso = format(date, "yyyy-MM-dd");
    api
      .get(`/meeting-rooms/${room.id}/availability/${iso}`)
      .then((r) => setUnavailable(r.data?.unavailable || []))
      .catch(() => setUnavailable([]));
  }, [room, date]);

  const layoutInfo = useMemo(() => {
    if (!room || !layout) return null;
    return (room.layouts || []).find((l) => l.layout_id === layout);
  }, [room, layout]);

  const price = useMemo(() => {
    if (!room || !slot) return 0;
    if (slot === "full_day") return room.price_full_day_xof || 0;
    return room.price_half_day_xof || 0;
  }, [room, slot]);

  const canSubmit =
    room && date && slot && layout && pax > 0 && !unavailable.includes(slot) &&
    booker.name.trim() && booker.surname.trim() && booker.email.trim() && booker.phone.trim();

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Veuillez compléter tous les champs et choisir un créneau disponible.");
      return;
    }
    setSubmitting(true);
    try {
      const iso = format(date, "yyyy-MM-dd");
      const { data: booking } = await api.post("/bookings", {
        offer_type: "salles",
        date: iso,
        adults: pax, children: 0,
        boat_time: "8H",
        meeting_room_id: room.id,
        meeting_room_layout: layout,
        meeting_room_slot: slot,
        meeting_room_setup_notes: setupNotes,
        participants: [{ ...booker }],
      });
      const { data: payment } = await api.post("/payments/fineo/checkout", {
        booking_id: booking.id, intent: "booking",
      });
      if (payment?.checkout_url) {
        window.open(payment.checkout_url, "_blank", "noopener,noreferrer");
        navigate(`/payment/fineo/result?booking_id=${booking.id}&intent=booking`);
      } else {
        toast.error("Lien de paiement indisponible.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Erreur lors de la réservation";
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la réservation");
    } finally {
      setSubmitting(false);
    }
  };

  const layoutName = (id) => layoutsCatalog.find((l) => l.id === id)?.name_fr || id;

  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-salles">
      {/* Sober introduction — no hero image, title & description centered (CMS iter-63) */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-white border-b border-[#0A0A0A]/8" data-testid="salles-intro">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
            {heroKicker}
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#0A0A0A] whitespace-pre-line">
            {heroTitle}
          </h1>
          <p className="mt-6 md:mt-8 max-w-2xl mx-auto text-base md:text-lg text-[#0A0A0A]/70 font-light leading-relaxed">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Step 1 — Pick a room */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-serif text-3xl mb-2">1. Choisissez votre salle</h2>
        <p className="text-sm text-neutral-500 mb-8">3 espaces disponibles, du salon privé à la salle plénière.</p>
        <div className="grid md:grid-cols-3 gap-6" data-testid="salles-rooms">
          {rooms.map((r) => {
            const active = room?.id === r.id;
            return (
              <button
                key={r.id}
                type="button"
                data-testid={`salles-room-${r.id}`}
                onClick={() => { setRoom(r); setLayout(""); setSlot(""); }}
                className={`text-left overflow-hidden border-2 transition-all ${ active ? "border-[#B8922A] shadow-xl" : "border-neutral-200 hover:border-[#B8922A]/50" }`}
              >
                <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: `url(${r.image_url || HERO})` }} />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl">{r.name}</h3>
                    {active && <CheckCircle2 className="w-5 h-5 text-[#B8922A]" />}
                  </div>
                  <p className="text-sm text-neutral-500 mt-1 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Jusqu&apos;à {r.capacity_max} personnes
                  </p>
                  {r.description && <p className="text-sm mt-3 text-neutral-700">{r.description}</p>}
                  <div className="mt-4 flex items-baseline gap-3 text-sm">
                    <span className="text-neutral-500">½ journée</span>
                    <span className="font-serif text-base text-[#B8922A] tabular-nums">{fmtXof(r.price_half_day_xof)}</span>
                  </div>
                  <div className="flex items-baseline gap-3 text-sm">
                    <span className="text-neutral-500">Journée</span>
                    <span className="font-serif text-base text-[#B8922A] tabular-nums">{fmtXof(r.price_full_day_xof)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 2+3+4 — date, slot, layout, info */}
      {room && (
        <section className="max-w-6xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif text-3xl mb-2">2. Date &amp; créneau</h2>
            <div className="border border-[#B8922A]/30 p-4 inline-block bg-[#FAFAF7] mb-6" data-testid="salles-calendar">
              <Calendar mode="single" selected={date} onSelect={setDate} fromDate={new Date()} locale={frLocale} />
            </div>

            {date && (
              <div className="space-y-2" data-testid="salles-slots">
                {slotsCatalog.map((s) => {
                  const taken = unavailable.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`salles-slot-${s.id}`}
                      disabled={taken}
                      onClick={() => setSlot(s.id)}
                      className={`w-full text-left px-4 py-3 border-2 transition-all ${ taken ? "border-neutral-200 bg-neutral-50 text-neutral-400 line-through cursor-not-allowed" : slot === s.id ? "border-[#B8922A] bg-[#FAF6E8]" : "border-neutral-200 hover:border-[#B8922A]/50" }`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{s.label_fr}</span>
                        <span className="font-serif text-[#B8922A] tabular-nums">
                          {fmtXof(s.id === "full_day" ? room.price_full_day_xof : room.price_half_day_xof)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif text-3xl mb-2">3. Disposition</h2>
            <p className="text-sm text-neutral-500 mb-4">La capacité varie selon la disposition.</p>
            <div className="grid grid-cols-2 gap-2 mb-6" data-testid="salles-layouts">
              {(room.layouts || []).map((lay) => {
                const meta = layoutsCatalog.find((l) => l.id === lay.layout_id);
                return (
                  <button
                    key={lay.layout_id}
                    type="button"
                    data-testid={`salles-layout-${lay.layout_id}`}
                    onClick={() => setLayout(lay.layout_id)}
                    className={`p-3 border-2 text-sm transition-all ${ layout === lay.layout_id ? "border-[#B8922A] bg-[#FAF6E8]" : "border-neutral-200 hover:border-[#B8922A]/50" }`}
                  >
                    <div className="font-medium">{meta?.name_fr || lay.layout_id}</div>
                    <div className="text-xs text-neutral-500">Jusqu&apos;à {lay.capacity} pax</div>
                  </button>
                );
              })}
            </div>

            <h2 className="font-serif text-3xl mb-2">4. Convives</h2>
            <div className="flex items-center gap-3 mb-6" data-testid="salles-pax">
              <button type="button" onClick={() => setPax(Math.max(1, pax - 1))}
                className="w-10 h-10 border-2 border-[#B8922A]/40">−</button>
              <span className="text-3xl font-serif w-16 text-center tabular-nums">{pax}</span>
              <button type="button" onClick={() => setPax(Math.min(layoutInfo?.capacity || room.capacity_max, pax + 1))}
                className="w-10 h-10 border-2 border-[#B8922A]/40">+</button>
              {layoutInfo && (
                <span className="text-xs text-neutral-500 ml-2">
                  Capacité max : {layoutInfo.capacity} (disposition {layoutName(layout)})
                </span>
              )}
            </div>

            <h2 className="font-serif text-3xl mb-2">5. Coordonnées &amp; brief</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-3" data-testid="salles-booker">
              <Input data-testid="salles-firstname" placeholder="Prénom" value={booker.name} onChange={(e) => setBooker({ ...booker, name: e.target.value })} />
              <Input data-testid="salles-lastname"  placeholder="Nom"    value={booker.surname} onChange={(e) => setBooker({ ...booker, surname: e.target.value })} />
              <Input data-testid="salles-email" type="email" placeholder="Email pro" value={booker.email} onChange={(e) => setBooker({ ...booker, email: e.target.value })} />
              <Input data-testid="salles-phone" placeholder="Téléphone" value={booker.phone} onChange={(e) => setBooker({ ...booker, phone: e.target.value })} />
            </div>
            <textarea
              data-testid="salles-notes"
              placeholder="Demandes particulières (pause-café, vidéoprojecteur, plateau-repas...)"
              rows={3}
              value={setupNotes}
              onChange={(e) => setSetupNotes(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 text-sm"
            />

            {/* Recap + CTA */}
            <div className="mt-8 p-5 bg-[#FAF6E8] border border-[#B8922A]/30">
              <div className="flex justify-between items-baseline">
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Total</div>
                  <div className="font-serif text-4xl text-[#B8922A] tabular-nums" data-testid="salles-total">{fmtXof(price)}</div>
                </div>
                <div className="text-right text-xs text-neutral-600">
                  {date && <div>{format(date, "EEE d MMM yyyy", { locale: frLocale })}</div>}
                  {slot && <div>{slotsCatalog.find((s) => s.id === slot)?.label_fr}</div>}
                  {layout && <div>{layoutName(layout)} · {pax} pax</div>}
                </div>
              </div>
              <Button
                data-testid="salles-submit"
                disabled={!canSubmit || submitting}
                onClick={submit}
                className="mt-5 w-full bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white py-6"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}
                Payer &amp; Réserver
              </Button>
            </div>

            {/* Equipment recap */}
            {(room.equipment || []).length > 0 && (
              <div className="mt-6 text-sm">
                <div className="font-medium mb-2">Équipement inclus</div>
                <ul className="grid grid-cols-2 gap-1 text-neutral-600 text-xs">
                  {room.equipment.map((eq, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 mt-0.5 text-[#B8922A]" />{eq}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
